import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Eye, EyeOff, Lock, Mail, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { forgotPassword } from '../services/api';

const LOGO_URL = "/assets/logo-le-bleu-des-champs.png";

export const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [credentials, setCredentials] = useState({
    email: '',
    password: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCredentials(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const user = await login(credentials.email, credentials.password);
      toast.success(`Bienvenue, ${user.first_name}!`);
      navigate('/dashboard');
    } catch (error) {
      console.error('Login error:', error);
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Identifiants incorrects');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail) {
      toast.error('Veuillez entrer votre courriel');
      return;
    }
    setForgotLoading(true);
    try {
      await forgotPassword(forgotEmail);
      toast.success('Si ce courriel existe, vous recevrez un lien de réinitialisation.');
      setForgotPasswordOpen(false);
      setForgotEmail('');
    } catch (error) {
      toast.error('Erreur lors de l\'envoi du courriel');
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <Link to="/" className="inline-block mb-8">
            <img src={LOGO_URL} alt="Le Bleu des Champs" className="h-16" />
          </Link>
          
          <h1 className="text-3xl font-bold text-[#1a3a5c] mb-2">
            Connexion
          </h1>
          <p className="text-slate-500 mb-8">
            Accédez à votre espace de gestion
          </p>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="email">Courriel</Label>
              <div className="relative mt-2">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={credentials.email}
                  onChange={handleChange}
                  placeholder="votre@courriel.com"
                  className="pl-12 input-styled"
                  data-testid="email-input"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  required
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Mot de passe</Label>
                <button
                  type="button"
                  onClick={() => setForgotPasswordOpen(true)}
                  className="text-sm text-[#00b4d8] hover:underline"
                  data-testid="forgot-password-link"
                >
                  Mot de passe oublié?
                </button>
              </div>
              <div className="relative mt-2">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={credentials.password}
                  onChange={handleChange}
                  placeholder="••••••••"
                  className="pl-12 pr-12 input-styled"
                  data-testid="password-input"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full rounded-full bg-[#00b4d8] hover:bg-[#009bb8] text-white py-6 text-lg"
              data-testid="login-submit-btn"
            >
              {loading ? 'Connexion...' : 'Se connecter'}
            </Button>
          </form>

          <div className="mt-8 text-center">
            <Link to="/" className="text-[#00b4d8] hover:underline">
              Retour au site
            </Link>
          </div>

        </div>
      </div>

      {/* Right side - Image */}
      <div className="hidden lg:flex flex-1 bg-gradient-to-br from-[#1a3a5c] to-[#0f172a] items-center justify-center p-12 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-20 left-10 w-72 h-72 bg-[#00b4d8] rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#00d4ff] rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative text-center">
          <img 
            src={LOGO_URL} 
            alt="Le Bleu des Champs" 
            className="h-24 mx-auto mb-8 brightness-0 invert"
          />
          <h2 className="text-3xl font-bold text-white mb-4">
            Espace de gestion
          </h2>
          <p className="text-slate-300 max-w-sm mx-auto">
            Gérez vos chantiers, votre inventaire, vos employés et vos demandes 
            clients depuis un seul endroit.
          </p>
        </div>
      </div>

      {/* Forgot Password Modal */}
      <Dialog open={forgotPasswordOpen} onOpenChange={setForgotPasswordOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#1a3a5c]">Mot de passe oublié</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleForgotPassword} className="space-y-4">
            <p className="text-slate-500 text-sm">
              Entrez votre adresse courriel et nous vous enverrons un lien pour réinitialiser votre mot de passe.
            </p>
            <div>
              <Label htmlFor="forgot-email">Courriel</Label>
              <div className="relative mt-2">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="forgot-email"
                  type="email"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  placeholder="votre@courriel.com"
                  className="pl-12 input-styled"
                  data-testid="forgot-email-input"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  required
                />
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setForgotPasswordOpen(false)}
                className="flex-1"
              >
                Annuler
              </Button>
              <Button
                type="submit"
                disabled={forgotLoading}
                className="flex-1 bg-[#00b4d8] hover:bg-[#009bb8]"
                data-testid="forgot-submit-btn"
              >
                {forgotLoading ? 'Envoi...' : 'Envoyer le lien'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LoginPage;
